<template>
  <v-col
    class="pa-0"
  >
    <v-row class="ma-0">
      <bread-crumbs />

      <v-col
        class="mr-2"
      >
        <v-row class="justify-end">
          <v-col
            v-if="hasAccessToBurtForecastLink"
            class="col-auto"
          >
            <v-btn
              class="purple"
              style="background-color: #8c46ff !important; border-color: #8c46ff !important;"
              aria-label="Open BurtForecast in a new tab"
              dark
              small
              title="Open BurtForecast in a new tab"
              @click="goToBurtForecast"
            >
              <font-awesome-icon :icon="['far', 'up-right-from-square']" />
              <span class="ml-2">Open BurtForecast</span>
            </v-btn>
          </v-col>

          <v-col
            v-if="hasAccessToBurtHubLink"
            class="col-auto"
          >
            <v-btn
              class="purple"
              style="background-color: #8c46ff !important; border-color: #8c46ff !important;"
              aria-label="Open BurtHub in a new tab"
              dark
              small
              title="Open BurtHub in a new tab"
              @click="goToBurtHub"
            >
              <font-awesome-icon :icon="['far', 'up-right-from-square']" />
              <span class="ml-2">Open BurtHub</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row
      v-if="showSummaries"
      class="mt-0"
    >
      <v-col class="py-0 ml-4 mr-5">
        <v-divider />
      </v-col>
    </v-row>

    <!-- Summary Cards Row -->
    <v-row
      v-if="showSummaries"
      class="mx-0 mb-0 mt-4"
    >
      <v-col>
        <v-row class="summary-bar">
          <v-col
            v-for="summary in Object.values(localDeliverySummaries)"
            :key="summary.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="2"
          >
            <v-card
              :class="[
                'pa-3',
                summary.styles.background,
                `${summary.styles.text}--text`,
                'text-center',
                'cursor-pointer',
                'summary-card-with-info',
                { 'summary-active': activeSummaryId === summary.id }
              ]"
              height="100%"
              @click="handleSummaryClick(summary)"
            >
              <v-btn
                icon
                small
                class="info-icon-button"
                :class="`${summary.styles.text}--text`"
                @click.stop="handleInfoClick(summary)"
              >
                <font-awesome-icon
                  :icon="['fas', 'circle-info']"
                  size="sm"
                />
              </v-btn>

              <v-card-title
                class="pa-0 subtitle-2 font-weight-bold justify-center"
              >
                {{ summary.title }}
              </v-card-title>

              <v-card-subtitle
                v-if="summary.subtitle"
                :class="`pa-0 pt-3 caption font-weight-medium ${summary.styles.text}--text`"
              >
                {{ summary.subtitle }}
              </v-card-subtitle>

              <v-card-text
                class="pa-0 pt-2 headline font-weight-bold text-center"
                :style="`color: ${summary.styles.value}`"
              >
                {{ numberFormatter(summary.total) }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row
      v-if="showSummaries"
      class="mt-0"
    >
      <v-col class="pt-0 mx-4">
        <v-divider />
      </v-col>
    </v-row>

    <!-- Main Content: Dashboard -->
    <report-list v-if="showDashboard" />

    <!-- Main Content: Report -->
    <v-row
      v-else
      class="ma-0"
    >
      <v-col>
        <data-table
          ref="table"
          :actions="actions"
        />

        <line-item-history-modal
          v-if="isLineItemHistoryModalVisible"
          v-model="isLineItemHistoryModalVisible"
          :line-item-record="currentRecord"
        />

        <creatives-report-modal
          v-if="isCreativesReportModalVisible"
          v-model="isCreativesReportModalVisible"
          :line-item-record="currentRecord"
        />

        <tearsheet-modal
          v-if="isCreativeModalVisible"
          v-model="isCreativeModalVisible"
          :line-item-record="currentRecord"
        />

        <summary-info-modal />
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import filter from 'src/utils/filter'
import format from 'src/utils/format'
import logger from 'src/plugins/logger'
import { fireanalytics } from 'src/store/firebase'
import mixin from '../components/mixin'
import * as summaries from '../store/utils/summaries'

const { mapState } = createNamespacedHelpers('delivery')
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapMessagesGetters } = createNamespacedHelpers('messages')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Pacing',
  components: {
    breadCrumbs: () => import('@/layout/components/BreadCrumbs'),
    creativesReportModal: () => import('../components/modals/CreativesReportModal'),
    lineItemHistoryModal: () => import('../components/modals/LineItemHistoryModal'),
    reportList: () => import('@/dashboard/components/ReportList'),
    summaryInfoModal: () => import('@/tools/components/summary/SummaryInfoModal'),
    tearsheetModal: () => import('../components/modals/TearsheetModal'),
  },
  mixins: [mixin],
  data() {
    return {
      actions: [
        {
          event: 'delivery/openTabToLineItem',
          name: 'Go to Line Item',
        },
        {
          event: 'delivery/openTabToOrder',
          name: 'Go to Order',
        },
        {
          event: 'delivery/showLineItemHistoryModal',
          name: '10-Day History Report',
        },
        {
          event: 'delivery/showCreativesReportModal',
          name: 'Creatives Report',
        },
        {
          event: 'delivery/showTearsheetModal',
          name: 'Preview & Tearsheet',
        },
      ],

      linkToBurtHub: 'https://us.burthub.com',
      linkToBurtForecast: 'https://jama-forecasting.us.burthub.com/',

      searchCollection: [],

      // Track the currently active summary filter
      activeSummaryId: null,
    }
  },
  computed: {
    ...mapAuthGetters([
      'hasAccessToBurtHubLink',
      'hasAccessToBurtForecastLink',
    ]),
    ...mapToolsGetters([
      'currentGroups',
      'currentItems',
      'showSummaries',
    ]),
    ...mapMessagesGetters(['currentMessage']),
    ...mapToolsState({
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      searchResults: (state) => state.searchResults,
      selectedSearch: (state) => state.selectedSearch,
      summaries: (state) => state.summaries,
    }),
    ...mapState({
      currentRecord: (state) => state.currentRecord,
      isLineItemHistoryModalVisible: (state) => state.isLineItemHistoryModalVisible,
      isCreativesReportModalVisible: (state) => state.isCreativesReportModalVisible,
      isCreativeModalVisible: (state) => state.isCreativeModalVisible,
      publisher: (state) => state.publisher,
      subId: (state) => state.subId,
    }),
    showDashboard() {
      return this.selectedSearch?.dataType === 'custom'
    },

    // Local summary calculations based on current filtered data
    localDeliverySummaries() {
      // Safely access summaries config
      const pacingSummaries = summaries?.pacing || summaries?.default?.pacing

      if (!pacingSummaries) {
        logger.console.info(['Pacing summaries config not available yet'])
        return {}
      }

      // Flatten currentGroups to get all filtered/grouped/merged items
      const currentData = this.currentGroups?.length
        ? this.currentGroups.reduce((allItems, group) => allItems.concat(group.items || []), [])
        : this.currentItems || []

      // Debug: Log data availability
      logger.console.info(['Pacing Summary Calculation:', {
        groupCount: this.currentGroups?.length || 0,
        itemCount: currentData.length,
        sampleItem: currentData[0],
      }])

      // Calculate counts using filter-based approach for consistency with table filtering
      const atRiskCount = this.calculateSummaryCount(currentData, pacingSummaries.AT_RISK)
      const stuckInPausedCount = this.calculateSummaryCount(currentData, pacingSummaries.STUCK_IN_PAUSED)
      const stuckInReadyCount = this.calculateSummaryCount(currentData, pacingSummaries.STUCK_IN_READY)
      const missingCreativesCount = this.calculateSummaryCount(currentData, pacingSummaries.MISSING_CREATIVES)
      const zeroImpressionsCount = this.calculateSummaryCount(currentData, pacingSummaries.ZERO_IMPRESSIONS)
      const endingSoonCount = this.calculateSummaryCount(currentData, pacingSummaries.ENDING_SOON)
      const viewabilityCount = this.calculateSummaryCount(currentData, pacingSummaries.VIEWABILITY)

      // Debug: Log calculated counts
      logger.console.info(['Pacing Summary Counts:', {
        atRiskCount,
        stuckInPausedCount,
        stuckInReadyCount,
        missingCreativesCount,
        zeroImpressionsCount,
        endingSoonCount,
        viewabilityCount,
      }])

      // Track summary calculation in analytics
      fireanalytics.event('summary_calculated', {
        category: 'Pacing',
        description: 'Pacing Summary Counts Calculated',
        label: `Total: ${viewabilityCount}, At Risk: ${atRiskCount}, Stuck: ${stuckInPausedCount + stuckInReadyCount}`,
        properties: JSON.stringify({
          total: viewabilityCount,
          atRisk: atRiskCount,
          stuckInPaused: stuckInPausedCount,
          stuckInReady: stuckInReadyCount,
          missingCreatives: missingCreativesCount,
          zeroImpressions: zeroImpressionsCount,
          endingSoon: endingSoonCount,
        }),
      })

      // Return summary objects matching the expected format
      return {
        AT_RISK: {
          id: 'AT_RISK',
          title: 'At Risk',
          subtitle: this.getDeliverySubtitle(),
          total: atRiskCount,
          format: 'number',
          columns: pacingSummaries.AT_RISK?.columns || [],
          parameters: pacingSummaries.AT_RISK?.parameters || {},
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        STUCK_IN_PAUSED: {
          id: 'STUCK_IN_PAUSED',
          title: 'Stuck In Paused',
          subtitle: this.getDeliverySubtitle(),
          total: stuckInPausedCount,
          format: 'number',
          columns: pacingSummaries.STUCK_IN_PAUSED?.columns || [],
          parameters: pacingSummaries.STUCK_IN_PAUSED?.parameters || {},
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        STUCK_IN_READY: {
          id: 'STUCK_IN_READY',
          title: 'Stuck In Ready',
          subtitle: this.getDeliverySubtitle(),
          total: stuckInReadyCount,
          format: 'number',
          columns: pacingSummaries.STUCK_IN_READY?.columns || [],
          parameters: pacingSummaries.STUCK_IN_READY?.parameters || {},
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        MISSING_CREATIVES: {
          id: 'MISSING_CREATIVES',
          title: 'Missing Creatives',
          subtitle: this.getDeliverySubtitle(),
          total: missingCreativesCount,
          format: 'number',
          columns: pacingSummaries.MISSING_CREATIVES?.columns || [],
          parameters: pacingSummaries.MISSING_CREATIVES?.parameters || {},
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        ZERO_IMPRESSIONS: {
          id: 'ZERO_IMPRESSIONS',
          title: 'Zero Impressions',
          subtitle: this.getDeliverySubtitle(),
          total: zeroImpressionsCount,
          format: 'number',
          columns: pacingSummaries.ZERO_IMPRESSIONS?.columns || [],
          parameters: pacingSummaries.ZERO_IMPRESSIONS?.parameters || {},
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        ENDING_SOON: {
          id: 'ENDING_SOON',
          title: 'Ending Soon',
          subtitle: this.getDeliverySubtitle(),
          total: endingSoonCount,
          format: 'number',
          columns: pacingSummaries.ENDING_SOON?.columns || [],
          parameters: pacingSummaries.ENDING_SOON?.parameters || {},
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        VIEWABILITY: {
          id: 'VIEWABILITY',
          title: 'Viewability',
          subtitle: this.getDeliverySubtitle(),
          total: viewabilityCount,
          format: 'number',
          columns: pacingSummaries.VIEWABILITY?.columns || [],
          parameters: pacingSummaries.VIEWABILITY?.parameters || {},
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
      }
    },
  },
  watch: {
    // Reset active summary when route changes (navigating to different client/team)
    $route() {
      this.activeSummaryId = null
    },
    // Reset active summary when filters are changed externally (filter form, etc.)
    currentGroups(newVal, oldVal) {
      // Only reset if groups actually changed structure (not just reactivity update)
      if (newVal !== oldVal && this.activeSummaryId) {
        logger.console.info(['Data groups changed, checking if active summary is still valid'])
      }
    },
  },
  methods: {
    /**
     * Handle click on summary card to apply filter or reset if already active
     * @param {Object} summary - Summary object with columns and parameters properties
     */
    handleSummaryClick(summary) {
      const { columns, id, parameters, title } = summary

      // Special handling for Viewability summary
      if (id === 'VIEWABILITY') {
        logger.console.info(['Viewability summary clicked - applying viewability column configuration'])

        // Track viewability click in analytics
        fireanalytics.event('summary_viewability_clicked', {
          action: 'click',
          category: 'Pacing',
          description: 'View Viewability Data',
          label: 'Apply viewability column configuration',
          properties: JSON.stringify({
            summaryId: id,
            columnCount: columns?.length || 0,
          }),
        })

        // Set this summary as active
        this.activeSummaryId = id

        // Reset filters to show all data
        this.$store.dispatch('tools/resetFilters')

        // Apply the viewability column configuration
        const summaryReport = {
          action: 'Apply Viewability Columns',
          columns,
          items: this.currentItems,
          reportKey: 'pacing',
          filterData: {}, // No filters, just column visibility
        }

        // Apply the column visibility changes
        this.$store.dispatch('tools/applyFilters', summaryReport)

        return
      }

      // Check if this summary is already active - if so, reset filters
      if (this.activeSummaryId === id) {
        logger.console.info([`Resetting filter for active summary "${title}"`])

        // Track reset in analytics
        fireanalytics.event('summary_filter_reset', {
          action: 'click',
          category: 'Pacing',
          description: 'Reset Summary Filter',
          label: `Reset filter for "${title}" summary`,
          properties: JSON.stringify({
            summaryId: id,
          }),
        })

        // Clear the active summary
        this.activeSummaryId = null

        // Reset filters to show all data
        this.$store.dispatch('tools/resetFilters')
        return
      }

      // Check if summary has filter parameters
      const hasParameters = parameters && Object.keys(parameters).length > 0

      if (!hasParameters) {
        logger.console.info([`Summary "${title}" has no filter parameters`])
        return
      }

      // Track summary click in analytics
      fireanalytics.event('summary_clicked', {
        action: 'click',
        category: 'Pacing',
        description: 'Apply Summary Filter',
        label: `Apply filter for "${title}" summary`,
        properties: JSON.stringify({
          summaryId: id,
          columnsCount: columns?.length || 0,
          columnKeys: columns?.map((c) => c.key).join(',') || '',
        }),
      })

      // Set this summary as active
      this.activeSummaryId = id

      // Convert parameters to filterData format
      const filterData = {}
      const paramKeys = Object.keys(parameters)

      paramKeys.forEach((key) => {
        const paramFunction = parameters[key]
        if (typeof paramFunction === 'function') {
          const filterResult = paramFunction()

          // Store the full filter objects (with operator and value)
          if (Array.isArray(filterResult)) {
            filterData[key] = filterResult
          } else if (filterResult && typeof filterResult === 'object') {
            filterData[key] = filterResult
          }
        }
      })

      // Apply filters directly with the converted filterData
      const summaryReport = {
        action: 'Apply Summary Filter',
        columns,
        items: this.currentItems,
        reportKey: 'pacing',
        filterData,
      }

      // Commit the filterData to state first
      this.$store.commit('tools/filterData', {
        filterData: { pacing: filterData },
        reportKey: 'pacing',
      })

      // Then apply the filters
      this.$store.dispatch('tools/applyFilters', summaryReport)
    },

    /**
     * Handle click on info icon to show summary details modal
     * @param {Object} summary - Summary object with id and details
     */
    handleInfoClick(summary) {
      // Track info click in analytics
      fireanalytics.event('summary_info_clicked', {
        action: 'click',
        category: 'Pacing',
        description: 'View Summary Information',
        label: `View info for "${summary.title}" summary`,
        properties: JSON.stringify({
          summaryId: summary.id,
        }),
      })

      // Dispatch action to show info modal
      this.$store.dispatch('tools/showSavedFilterInfo', summary)
    },

    goToBurtForecast() {
      /**
       * @note Regarding the NoOpener,NoReferrer Policy
       * It's generally recommended to add rel="noopener noreferrer"
       * to links using target="_blank". When using window.open(),
       * we achieve a similar effect by passing noopener and
       * noreferrer as the third argument. This prevents the new
       * tab from accessing the original page's window.opener
       * object, mitigating potential security risks.
       */

      window.open(this.linkToBurtForecast, '_blank', 'noopener,noreferrer')
    },
    goToBurtHub() {
      /**
       * @note Regarding the NoOpener,NoReferrer Policy
       * It's generally recommended to add rel="noopener noreferrer"
       * to links using target="_blank". When using window.open(),
       * we achieve a similar effect by passing noopener and
       * noreferrer as the third argument. This prevents the new
       * tab from accessing the original page's window.opener
       * object, mitigating potential security risks.
       */

      window.open(this.linkToBurtHub, '_blank', 'noopener,noreferrer')
    },

    // Local summary calculation methods
    /**
     * Convert summary parameters to filter format and count matching items
     * This uses the same FILTER.report logic that filters the main data table
     * @param {Array} data - Items to filter
     * @param {Object} summaryConfig - Summary config from summaries.js with parameters
     * @returns {Number} Count of items matching filter
     */
    calculateSummaryCount(data, summaryConfig) {
      if (!summaryConfig || !summaryConfig.parameters) {
        return data.length
      }

      logger.console.info(['calculateSummaryCount called', {
        summaryId: summaryConfig.id,
        dataLength: data.length,
        parameters: summaryConfig.parameters,
      }])

      // Convert summary parameters to filter format
      const filterData = {}
      const paramKeys = Object.keys(summaryConfig.parameters)

      paramKeys.forEach((key) => {
        const paramFunction = summaryConfig.parameters[key]
        if (typeof paramFunction === 'function') {
          const filterResult = paramFunction()

          logger.console.info([`Parameter "${key}" result:`, filterResult])

          // Handle different return formats from summary parameters:
          // 1. Array of { operator, value } objects (e.g., status, osi)
          // 2. Single { operator, value } object (e.g., startDate)
          if (Array.isArray(filterResult)) {
            // Keep the full array of { operator, value } objects
            // FILTER.report expects this format for normalizeFilterValues
            filterData[key] = filterResult
          } else if (filterResult && typeof filterResult === 'object') {
            // For single objects (usually date filters), wrap in array
            // to match the expected format
            filterData[key] = filterResult
          }
        }
      })

      logger.console.info(['Filter data created:', filterData])

      const filterKeys = Object.keys(filterData)

      // If no filters, return all data
      if (filterKeys.length === 0) {
        return data.length
      }

      // Use FILTER.report to check each item (same logic as table filtering)
      const reportFilter = filter.report.bind(filter)
      const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)

      const matchingItems = data.filter(isItemIncluded)

      logger.console.info([`Matching items count: ${matchingItems.length}`])

      // Log first item structure for debugging
      if (data.length > 0) {
        logger.console.info(['First data item structure:', Object.keys(data[0])])
      }

      return matchingItems.length
    },

    getDeliverySubtitle() {
      // Create contextual subtitle based on current filters/date range
      return this.currentItems === 1 ? 'line item' : 'line items'
    },

    numberFormatter(value) {
      return format.number(value)
    },

    /**
     * Show the viewability column in the data table
     * Updates the column visibility to make viewabilityPercentage visible
     */
    showViewabilityColumn() {
      const reportKey = this.$store.getters['tools/reportKey']
      const currentColumns = this.$store.state.tools.columns[reportKey]

      if (!currentColumns || !reportKey) {
        logger.console.info(['Cannot show viewability column - missing columns or reportKey'])
        return
      }

      // Find and update the viewabilityPercentage column
      const updatedColumns = currentColumns.map((column) => {
        if (column.key === 'viewabilityPercentage') {
          return {
            ...column,
            visible: true,
          }
        }
        return column
      })

      // Commit the updated columns to store
      this.$store.commit('tools/columns', { [reportKey]: updatedColumns })

      logger.console.info(['Viewability column visibility set to true'])
    },

    /**
     * Clean URL query parameters by keeping only recognized parameters
     * @param {Object} currentQuery - Current route query object
     * @returns {Object} Cleaned query object with only valid parameters
     */
    cleanQueryParameters(currentQuery) {
      // Define the whitelist of recognized query parameters for the Pacing component
      const validParameters = ['publisher', 'team', 'startDate', 'endDate', 'lineItem', 'order', 'status']

      const cleanedQuery = {}

      // Only keep parameters that are in our whitelist
      validParameters.forEach((param) => {
        if (currentQuery[param] !== undefined) {
          cleanedQuery[param] = currentQuery[param]
        }
      })

      return cleanedQuery
    },

    /**
     * Update URL with clean parameters for bookmarkable states
     * @param {Object} newParams - New parameters to add/update
     */
    updateCleanUrl(newParams = {}) {
      const currentQuery = this.$route.query || {}
      const cleanedQuery = this.cleanQueryParameters(currentQuery)

      // Merge in new parameters
      Object.assign(cleanedQuery, newParams)

      // Only update route if query actually changed
      const currentQueryString = JSON.stringify(currentQuery)
      const cleanedQueryString = JSON.stringify(cleanedQuery)

      if (currentQueryString !== cleanedQueryString) {
        this.$router.push({ query: cleanedQuery })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

::v-deep.filter-link {
  margin-top: 8px;
}

::v-deep.filter-content {
  margin-top: 16px;
}

::v-deep.filter-label {
  padding-top: 20px;
}

.summary-bar {
  .v-card.cursor-pointer {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .summary-card-with-info {
    position: relative;

    .info-icon-button {
      position: absolute;
      top: 4px;
      right: 4px;
      z-index: 2;
      opacity: 0.7;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }

    // Active state styling
    &.summary-active {
      background-color: #055289 !important; // darkestblue from hydrogen.js
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4) !important;
      transform: translateY(-2px);

      // Override the card's original background color classes
      &.yellow,
      &.green,
      &.purple,
      &.blue,
      &.red,
      &.orange,
      &.cyan,
      &.teal {
        background-color: #055289 !important;
      }
    }
  }
}

.delivery-table-filter {
  .first-row {
    display: flex;

    .v-text-field {
      margin: 0 24px;
      padding-top: 0;
      font-size: 12px;
    }
  }

  .second-row {
    display: flex;
    ::v-deep.v-text-field {
      margin: 0 24px;
      padding-top: 0;
      font-size: 12px;
    }

    .osi-selector {
      max-width: 350px;
    }

    .filter-controls-spacer {
      min-height: 52px;
    }
  }

  ::v-deep.v-date-range {
    .v-text-field {
      min-width: 250px !important;
    }
  }
}
</style>
